import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Container,
} from "reactstrap";
import { FormNumber } from "../Controls";
import LaddaButton, { SLIDE_LEFT } from "react-ladda";
import style from "./styles.module.scss";
import { Asset, FormField } from "../../..";
import { dateIsEmpty, valueIsEmpty } from "../../../utils/Helper";
import { showToast } from "../../../utils/Helper";

const initialFields = {
  AssetCouponDate: today(),
  AssetNewCouponDate: today(),
  AssetCouponRate: "",
  AssetCapitalizationRate: "",
  AssetSink: "",
  AssetPrincipalFactor: "",
  AssetAccrualFactor: "",
  AssetCouponNumber: "",
  AssetExpectedPayment: "",
  quantity: "",
  realAccruals: "",
  expectedAccruals: "",
};

const ModalPayments = ({ open, toggle, payment, onConfirm, assetId, services, asset }) => {
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [valorAccrualFactor,setValorAccrualFactor] = useState("");
  const [indice, setIndice] = useState(1);


  useEffect(() => {
    if (!open) {
      setShowAdvanced(false);
    }
  }, [open]);

  useEffect(() => {
    if (payment) {
      setFields({ ...payment, AssetNewCouponDate: payment.AssetCouponDate });
      setErrors({});
      if(asset.AssetHasIndex == true){
        setIndice(asset.AssetIndexExpression);
        setValorAccrualFactor(payment.AssetAccrualFactor);
      }else{
        setValorAccrualFactor(payment.AssetAccrualFactor);
      }
    } else {
      setFields(initialFields);
      setErrors({});
    }
  }, [payment]);

  const onChange = (name, value) => {
    if ( name == "realAccruals" ){
      setFields({ ...fields, [name]: value, expectedAccruals: 0 });
    }else{
      setFields({ ...fields, [name]: value });
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const handleOnChangeCouponDate = (name, value) => {
    if (payment) {
      setFields({ ...fields, AssetNewCouponDate: value });
    } else {
      onChange("AssetCouponDate", value);
    }
  };

  const handleRecalculate = async () => {

    const nextErrors = validateRecalculate(fields);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      setLoading(true);
      const body = {
        assetId: assetId,
        date: fields.AssetCouponDate,
        quantity: fields.quantity,
      };

      try {
        const { result, calculator } = await services.calculator(body);
        setLoading(false);
        if (result.Code === 400) {
          showToast(result.Description, true);
        } else {
          if(asset.AssetHasIndex == true){
            setIndice(asset.AssetIndexExpression);
          }

          let expectedAccruals = ((calculator.intrinsecaccruals / 1)*indice);
          if (valorAccrualFactor !=0 ){
            expectedAccruals =  ((calculator.intrinsecaccruals / valorAccrualFactor)*indice);
          }
          if (asset.CurrencyISO === "PYG") {
            expectedAccruals = Math.round(expectedAccruals);
          }

          setFields((prevFields) => ({
            ...prevFields,
            expectedAccruals,
            AssetAccrualFactor: calculateAccrualFactor(prevFields.realAccruals, expectedAccruals),
          }));

        }
      } catch (error) {
        setLoading(false);
        showToast("Error recalculating", true);
        setValorAccrualFactor("");
        setIndice(1);
      }
    }
  };

  const calculateAccrualFactor = (realAccruals, expectedAccruals) => {
    if (!expectedAccruals) return 0;
    return parseFloat(realAccruals || 0) / expectedAccruals;
  };

  const handleSave = async () => {
    const errors = validate(fields);
    setErrors(errors);
    if (Object.keys(errors).length === 0) {
      setLoading(true);
      const result = await onConfirm({
        ...fields,
        AssetNewCouponDate: getDateAsString(fields.AssetNewCouponDate),
        AssetCouponDate: getDateAsString(fields.AssetCouponDate),
      });
      setLoading(false);

      if (result === 200) {
        toggle();
      }
    }
  };

  const toggleAdvanced = () => setShowAdvanced(!showAdvanced);

  return (
    <Modal isOpen={open} toggle={toggle} className={style.modal}>
      <ModalHeader toggle={toggle}>{payment ? "Edit Payment" : "Add Payment"}</ModalHeader>
      <ModalBody>
        <Container className={style.container}>
          <Row>
            <Col lg="6">
              <FormField
                name="AssetCouponDate"
                label="Coupon date"
                type="date"
                value={
                  payment ? parseDate(fields.AssetNewCouponDate) : parseDate(fields.AssetCouponDate)
                }
                onChange={handleOnChangeCouponDate}
                error={errors.AssetCouponDate}
              />
            </Col>
            <Col lg="6">
              <FormNumber
                name="AssetCouponRate"
                label="Coupon rate"
                value={fields.AssetCouponRate}
                onChange={onChange}
                error={errors.AssetCouponRate}
                dataTypeDecimalName="percentage"
                decimalScale={5}
              />
            </Col>
          </Row>

          <Row>
            <Col lg="6">
              <FormNumber
                name="AssetCapitalizationRate"
                label="Capitalization rate"
                value={fields.AssetCapitalizationRate}
                onChange={onChange}
                error={errors.AssetCapitalizationRate}
                dataTypeDecimalName="percentage"
                decimalScale={8}
              />
            </Col>
            <Col lg="6">
              <FormNumber
                name="AssetSink"
                label="Sink"
                value={fields.AssetSink}
                onChange={onChange}
                error={errors.AssetSink}
                dataTypeDecimalName="currency"
                decimalScale={8}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="6">
              <FormNumber
                name="AssetPrincipalFactor"
                label="Principal factor"
                value={fields.AssetPrincipalFactor}
                onChange={onChange}
                error={errors.AssetPrincipalFactor}
                dataTypeDecimalName="currency"
                decimalScale={8}
              />
            </Col>
            <Col lg="6">
              <FormNumber
                name="AssetAccrualFactor"
                label="Accrual factor"
                value={fields.AssetAccrualFactor}
                onChange={onChange}
                error={errors.AssetAccrualFactor}
                dataTypeDecimalName="currency"
                decimalScale={8}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="6">
              <FormNumber
                name="AssetCouponNumber"
                label="Coupon number"
                value={fields.AssetCouponNumber}
                onChange={onChange}
                error={errors.AssetCouponNumber}
                dataTypeDecimalName="currency"
              />
            </Col>
            <Col lg="6">
              <FormNumber
                name="AssetExpectedPayment"
                label="Expected payment"
                value={fields.AssetExpectedPayment}
                onChange={onChange}
                error={errors.AssetExpectedPayment}
                dataTypeDecimalName="currency"
              />
            </Col>
          </Row>
          <Button color="link" onClick={toggleAdvanced} className="mb-3">
            {showAdvanced ? "˄ Advanced" : "˅ Advanced"}
          </Button>
          {showAdvanced && (
            <React.Fragment>
              <Row>
                <Col lg="6">
                  <FormNumber
                    name="quantity"
                    label="Quantity"
                    value={fields.quantity}
                    onChange={onChange}
                    error={errors.quantity}
                    dataTypeDecimalName="currency"
                  />
                </Col>
                <Col lg="6">
                  <FormNumber
                    name="realAccruals"
                    label="Real Accruals"
                    value={fields.realAccruals} // Ensure the value comes from fields
                    onChange={onChange}
                    error={errors.realAccruals}
                    dataTypeDecimalName="currency"
                    decimalScale={2}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg="6">
                  <FormNumber
                    name="expectedAccruals"
                    label="Expected Accruals"
                    value={fields.expectedAccruals}
                    readOnly={true}
                    onChange={onChange}
                    error={errors.expectedAccruals}
                    dataTypeDecimalName="currency"
                    decimalScale={2}
                  />
                </Col>
                <Col lg="6">
                  <Button color="primary" onClick={handleRecalculate} className={style.recalculate}>
                    Recalculate
                  </Button>
                </Col>
              </Row>
            </React.Fragment>
          )}
        </Container>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>
          Cancel
        </Button>
        <LaddaButton
          className="btn btn-ladda btn-primary pl-3 pr-3"
          loading={loading}
          disabled={false}
          onClick={() => handleSave()}
          data-style={SLIDE_LEFT}
        >
          <i className="fa fa-floppy-o mr-1" aria-hidden="true"></i>
          Save
        </LaddaButton>
      </ModalFooter>
    </Modal>
  );
};

function parseDate(date) {
  if (date instanceof Date) {
    return date.toISOString();
  }

  if (date.indexOf("T") > -1) {
    return date;
  }

  if (!date || date === "" || date === "0000-00-00" || dateIsEmpty(date)) {
    return "";
  }

  return new Date(`${date}T00:00`).toISOString();
}

function getDateAsString(date) {
  if (date instanceof Date) {
    return date.toISOString().substr(0, 10);
  }

  if (!date || date === "" || date === "0000-00-00" || dateIsEmpty(date)) {
    return "";
  }

  return date.substr(0, 10);
}

function validate(fields) {
  const errors = {};

  if (dateIsEmpty(fields.AssetCouponDate)) {
    errors.AssetCouponDate = "Field is required";
  }
  if (valueIsEmpty(fields.AssetCouponRate)) {
    errors.AssetCouponRate = "Field is required";
  }
  if (valueIsEmpty(fields.AssetCapitalizationRate)) {
    errors.AssetCapitalizationRate = "Field is required";
  }
  if (valueIsEmpty(fields.AssetSink)) {
    errors.AssetSink = "Field is required";
  }
  if (valueIsEmpty(fields.AssetPrincipalFactor)) {
    errors.AssetPrincipalFactor = "Field is required";
  }
  if (valueIsEmpty(fields.AssetCouponNumber)) {
    errors.AssetCouponNumber = "Field is required";
  }
  if (valueIsEmpty(fields.AssetExpectedPayment)) {
    errors.AssetExpectedPayment = "Field is required";
  }

  return errors;
}

function today() {
  return new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
}

function validateRecalculate(fields) {
  const errors = {};

  if (dateIsEmpty(fields.AssetCouponDate)) {
    errors.AssetCouponDate = "Field is required";
  }

  if (valueIsEmpty(fields.quantity)) {
    errors.quantity = "Field is required";
  }

  if (valueIsEmpty(fields.realAccruals)) {
    errors.realAccruals = "Field is required";
  }

  return errors;
}

export default ModalPayments;
